import AddIcon from '@mui/icons-material/Add';
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useEffect, useRef, useState } from 'react';
import ModalNAMContractCreation from '../components/modals/ModalNAMContractCreation';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { BuyerOrganizationModel, PriceModel, buildBuyerFromFeature, buildPriceFromFeature } from '../models/price';
import priceBubbleSvg from '../rsc/price_bubble.svg';
import priceBubbleGoldSvg from '../rsc/price_bubble_gold.svg';
import { MapHelper } from '../utils/mapHelper';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { copyToClipboard } from '../utils/helper';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

const MapPage: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapStyle, setMapStyle] = useState<string>('mapbox://styles/mapbox/light-v11');
  const [selectedPrices, setSelectedPrices] = useState<PriceModel[]>([]);
  const selectedPrice = selectedPrices[0] ?? null;
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [selectedPriceIds, setSelectedPriceIds] = useState<Set<number>>(new Set());
  const selectedPricesMap = useRef<Map<number, PriceModel>>(new Map());
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState<BuyerOrganizationModel | null>(null);
  const [lastGeneratedLink, setLastGeneratedLink] = useState<string>('');
  const [isSelectionMode, setIsSelectionMode] = useState<boolean>(false);
  const isSelectionModeRef = useRef<boolean>(false);

  // Get auth token from Redux
  const { token } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  // Use ref for isMounted to avoid stale closure in async code
  const isMountedRef = useRef(true);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      zoom: 8,
      center: [101.4478, 0.5071], // Sumatra, Indonesia
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    // Try to get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords: [number, number] = [
            position.coords.longitude,
            position.coords.latitude,
          ];
          setUserLocation(coords);
          map.current?.jumpTo({ center: coords, zoom: 10 });
        },
        (error) => {
          console.log('Geolocation error:', error);
        }
      );
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update map style
  useEffect(() => {
    if (map.current) {
      map.current.setStyle(mapStyle);
    }
  }, [mapStyle]);

  // Add vector tile source and layer
  useEffect(() => {
    console.log('Vector tile useEffect triggered', { hasMap: !!map.current, hasToken: !!token });

    if (!map.current || !token) {
      console.log('Skipping vector source setup:', { hasMap: !!map.current, hasToken: !!token });
      return;
    }

    const currentMap = map.current;

    const setupVectorSource = () => {
      isMountedRef.current = true;
      const hasuraUrl = process.env.REACT_APP_HASURA_URL || '';
      const baseUrl = hasuraUrl.replace(/\/graphql$/, '') || window.location.origin;
      const tileUrl = `${baseUrl}/mapTilingService/priceTileJsonV2/?token=${token}`;

      console.log('Setting up vector source with URL:', tileUrl);

      // Check if source already exists
      const existingSource = currentMap.getSource(MapHelper.MAP_SOURCE_ID);

      if (existingSource) {
        // Remove and re-add source with new URL
        if (currentMap.getLayer(MapHelper.MAP_LAYER_ID)) {
          currentMap.removeLayer(MapHelper.MAP_LAYER_ID);
        }
        currentMap.removeSource(MapHelper.MAP_SOURCE_ID);
      }

      // Add new vector source
      currentMap.addSource(MapHelper.MAP_SOURCE_ID, {
        type: 'vector',
        url: tileUrl,
        minzoom: 7,
        maxzoom: 10,
      });

      console.log('Vector source added:', MapHelper.MAP_SOURCE_ID);
      console.log('Tile URL:', tileUrl);

      // Load price bubble SVG icons (white and gold)
      const imgWhite = new Image();
      imgWhite.onload = () => {
        if (!currentMap.hasImage('price-bubble')) {
          currentMap.addImage('price-bubble', imgWhite);
          console.log('White bubble image loaded');

          // Add symbol layer for price display
          currentMap.addLayer({
            id: MapHelper.MAP_LAYER_ID,
            type: 'symbol',
            source: MapHelper.MAP_SOURCE_ID,
            'source-layer': MapHelper.MAP_PRICE_LAYER,
            layout: {
              'icon-image': 'price-bubble',
              'icon-size': 0.8,
              'icon-allow-overlap': true,
              'icon-anchor': 'bottom',
              'text-offset': [0, -1.7],
              'text-field': [
                'case',
                ['>', ['get', 'price_count'], 1],
                ['concat', ['to-string', ['get', 'price_count']], ' prices'],
                ['==', ['get', 'price_type'], 'bid_qbp'],
                ['concat', ['to-string', ['get', 'price_count']], ' prices'],
                ['to-string', ['get', 'price_per_unit']]
              ],
              'text-size': [
                'case',
                ['>', ['get', 'price_count'], 1], 14,
                ['==', ['get', 'price_type'], 'bid_qbp'], 14,
                14
              ],
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-allow-overlap': true,
              'symbol-sort-key': ['get', 'id'],
            },
            paint: {
              'text-color': '#075255',
            },
          });

          console.log('Symbol layer added successfully');
        }
      };
      imgWhite.src = priceBubbleSvg;

      const imgGold = new Image();
      imgGold.onload = () => {
        if (!currentMap.hasImage('price-bubble-gold')) {
          currentMap.addImage('price-bubble-gold', imgGold);
          console.log('Gold bubble image loaded');
        }
      };
      imgGold.src = priceBubbleGoldSvg;

      // Debug: Check what features are loaded after a short delay
      setTimeout(() => {
        if (!isMountedRef.current || !currentMap) {
          return;
        }
        if (typeof currentMap.querySourceFeatures !== 'function') return;
        const allFeatures = currentMap.querySourceFeatures(MapHelper.MAP_SOURCE_ID, {
          sourceLayer: MapHelper.MAP_PRICE_LAYER,
        });
        console.log('Total features loaded in source:', allFeatures.length);
        if (allFeatures.length > 0) {
          console.log('Sample feature:', allFeatures[0]);
          console.log('Sample feature properties:', allFeatures[0].properties);
        }
      }, 2000);

      // Listen to data events to see when tiles load
      currentMap.on('sourcedata', (e) => {
        if (e.sourceId === MapHelper.MAP_SOURCE_ID && e.isSourceLoaded) {
          console.log('Vector source loaded, checking features...');
          const features = currentMap?.querySourceFeatures(MapHelper.MAP_SOURCE_ID, {
            sourceLayer: MapHelper.MAP_PRICE_LAYER,
          });
          console.log('Features in source after load:', features.length);
        }
      });

      // Add hover effect
      currentMap.on('mouseenter', MapHelper.MAP_LAYER_ID, () => {
        currentMap.getCanvas().style.cursor = 'pointer';
      });

      currentMap.on('mouseleave', MapHelper.MAP_LAYER_ID, () => {
        currentMap.getCanvas().style.cursor = '';
      });
      // ...existing code...
      return () => {
        isMountedRef.current = false;
      };
    };

    // Wait for map to load before adding source
    if (currentMap.isStyleLoaded()) {
      console.log('Map style already loaded, setting up vector source immediately');
      setupVectorSource();
    } else {
      console.log('Waiting for map style to load...');
      currentMap.once('load', () => {
        console.log('Map load event fired, setting up vector source');
        setupVectorSource();
      });
    }
    // Cleanup for isMountedRef
    return () => {
      isMountedRef.current = false;
    };
  }, [token, mapStyle]);

  // Handle map clicks to show price details
  useEffect(() => {
    if (!map.current) return;

    const currentMap = map.current;

    const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
      const features = currentMap.queryRenderedFeatures(e.point, {
        layers: [MapHelper.MAP_LAYER_ID],
      });

      console.log('Clicked features:', features);
      console.log('Number of features found:', features.length);

      if (features.length > 0) {
        features.forEach(f => {
          console.log('Feature properties:', f.properties);
          console.log('Feature geometry:', f.geometry);
        })

        const prices = features.map(f => buildPriceFromFeature(f, userLocation));
        console.log('Built prices:', prices);

        const buyer = buildBuyerFromFeature(features[0]);
        console.log('Built buyer:', buyer);

        // Always update sidebar preview
        setSelectedPrices(prices);
        setSelectedBuyer(buyer);

        // Only accumulate selection when selection mode is active
        if (isSelectionModeRef.current) {
          const priceId = prices[0]?.id;
          if (!priceId) return;
          const isAlreadySelected = selectedPricesMap.current.has(priceId);

          setSelectedPriceIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(priceId)) {
              newSet.delete(priceId);
              selectedPricesMap.current.delete(priceId);
            } else {
              newSet.add(priceId);
              selectedPricesMap.current.set(priceId, prices[0]);
            }
            return newSet;
          });

          // If deselecting, clear the sidebar so the icon reverts to white
          if (isAlreadySelected) {
            setSelectedPrices([]);
            setSelectedBuyer(null);
          }
        }
      }
    };

    currentMap.on('click', MapHelper.MAP_LAYER_ID, handleMapClick);

    return () => {
      currentMap.off('click', MapHelper.MAP_LAYER_ID, handleMapClick);
    };
  }, [userLocation]);

  // Update icon colors when selection or previewed price changes
  useEffect(() => {
    if (!map.current) return;

    const currentMap = map.current;

    if (currentMap.getLayer(MapHelper.MAP_LAYER_ID)) {
      // Gold = all selected IDs + the currently previewed prices (even outside selection mode)
      const selectedArray = Array.from(selectedPriceIds);
      const goldIds = selectedPrices.length > 0
        ? Array.from(new Set([...selectedArray, ...selectedPrices.map(p => p.id)]))
        : selectedArray;

      currentMap.setLayoutProperty(
        MapHelper.MAP_LAYER_ID,
        'icon-image',
        goldIds.length > 0
          ? [
            'case',
            ['in', ['get', 'id'], ['literal', goldIds]],
            'price-bubble-gold',
            'price-bubble'
          ]
          : 'price-bubble'
      );
    }
  }, [selectedPriceIds, selectedPrices]);

  // Handle selection mode toggle
  const handleToggleSelectionMode = () => {
    const newMode = !isSelectionMode;
    if (!newMode) {
      // Clear selection when turning off
      setSelectedPriceIds(new Set());
      selectedPricesMap.current.clear();
    }
    isSelectionModeRef.current = newMode;
    setIsSelectionMode(newMode);
  };

  // Export selected prices as JSON
  const handleExport = () => {
    const selectedPrices = Array.from(selectedPricesMap.current.values());
    const millIds = selectedPrices.map(p => p.organisation.id).join(',');
    const welcomeLink = process.env.REACT_APP_WELCOME_LINK || '';
    const link = `${welcomeLink}/mills?mills=[${millIds}]`;

    setLastGeneratedLink(link);
    copyToClipboard(link);
  };

  // Handle contract modal dismiss
  const handleModalDismiss = (mutationParams: any) => {
    setIsContractModalOpen(false);
    if (mutationParams) {
      console.log('Contract mutation params:', mutationParams);
    }
  };

  // Handle create contract button click
  const handleCreateContractClick = () => {
    setIsContractModalOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Price Map</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant={isSelectionMode ? 'contained' : 'outlined'}
            color={isSelectionMode ? 'primary' : 'inherit'}
            startIcon={<PlaylistAddCheckIcon />}
            onClick={handleToggleSelectionMode}
          >
            {isSelectionMode ? 'Stop Selecting' : 'Select Mills'}
          </Button>
          <Badge badgeContent={selectedPriceIds.size} color="primary">
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleExport}
              disabled={selectedPriceIds.size === 0}
            >
              Export Selected
            </Button>
          </Badge>
        </Box>
      </Box>
      {lastGeneratedLink && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Last Generated Link"
            value={lastGeneratedLink}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyToClipboard(lastGeneratedLink)}
                >
                  Copy
                </Button>
              ),
            }}
          />
        </Box>
      )}


      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 200px)' }}>
        {/* Map Container */}
        <Paper
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            outline: isSelectionMode ? '2px solid' : 'none',
            outlineColor: 'primary.main',
            transition: 'outline 0.15s ease',
          }}
        >
          <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
          {isSelectionMode && (
            <Chip
              icon={<PlaylistAddCheckIcon />}
              label={`Selection Mode — ${selectedPriceIds.size} selected`}
              color="primary"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 1,
                fontWeight: 'bold',
                boxShadow: 2,
                pointerEvents: 'none',
              }}
            />
          )}
        </Paper>

        {/* Price Details Sidebar */}
        {selectedPrice && (
          <Paper sx={{ width: 350, p: 2, overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Price Details
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleCreateContractClick}
              >
                Create Contract
              </Button>
            </Box>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Price per Unit
                  </Typography>
                  <Chip
                    label={`${selectedPrice.pricePerUnit.toLocaleString()} Rp/kg`}
                    color="primary"
                    size="medium"
                  />
                </Box>

                {selectedPrice.programPricePerUnit && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Program Bonus
                    </Typography>
                    <Chip
                      label={`+${selectedPrice.programPricePerUnit.toLocaleString()} Rp/kg`}
                      color="success"
                      size="small"
                    />
                  </Box>
                )}

                {selectedPrice.groupPricePerUnit && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Group Bonus
                    </Typography>
                    <Chip
                      label={`+${selectedPrice.groupPricePerUnit.toLocaleString()} Rp/kg`}
                      color="info"
                      size="small"
                    />
                  </Box>
                )}

                {selectedPrice.distance && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Distance
                    </Typography>
                    <Typography variant="body2">
                      {(selectedPrice.distance / 1000).toFixed(2)} km
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {selectedPrice.qualityPriceLevels && selectedPrice.qualityPriceLevels.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Quality Based Prices
                </Typography>
                {selectedPrice.qualityPriceLevels.map((level, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {level.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            +{level.delta} Rp/kg bonus
                          </Typography>
                        </Box>
                        <Chip
                          label={`${level.price_pempem.toLocaleString()} Rp/kg`}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Organization
            </Typography>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="body1" fontWeight="bold" gutterBottom>
                  {selectedPrice.organisation.name}
                </Typography>
                <Chip
                  label={selectedPrice.organisation.type?.toUpperCase()}
                  size="small"
                  sx={{ mb: 1 }}
                />

                {selectedPrice.organisation.location && (
                  <Box sx={{ mt: 1 }}>
                    {selectedPrice.organisation.location.village && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Village: {selectedPrice.organisation.location.village}
                      </Typography>
                    )}
                    {selectedPrice.organisation.location.subDistrict && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Subdistrict: {selectedPrice.organisation.location.subDistrict}
                      </Typography>
                    )}
                  </Box>
                )}

                {selectedPrice.organisation.minimum_quality_level && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    Min Quality: {selectedPrice.organisation.minimum_quality_level}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {selectedPrice.programName && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Program
                </Typography>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="body2">{selectedPrice.programName}</Typography>
                  </CardContent>
                </Card>
              </>
            )}

            {selectedPrice.warningMessage && (
              <Card variant="outlined" sx={{ bgcolor: 'warning.light', mb: 2 }}>
                <CardContent>
                  <Typography variant="body2" color="warning.dark">
                    ⚠️ {selectedPrice.warningMessage}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Paper>
        )}
      </Box>

      {/* Contract Creation Modal */}
      <ModalNAMContractCreation
        prices={selectedPrices}
        isOpen={isContractModalOpen}
        onDismiss={handleModalDismiss}
      />
    </Box>
  );
};

export default MapPage;
